import * as Square from "@square/web-sdk";
import { useEffect, useRef, useState } from "react";

type UseAttachSquareCardArgs = {
  squareApplicationId: string;
  card: Square.Card | undefined;
  setCard: (card: Square.Card | undefined) => void;
  setLoading: (loading: boolean) => void;
  container: HTMLElement | null;
};

/**
 * Runs the one-shot Square card attach sequence:
 *
 *   Square.payments(applicationId) → payments.card({}) → card.attach(element)
 *
 * Stores the resulting card via `setCard` and toggles `setLoading` around the
 * async work. Idempotent while a card container is actively being attached,
 * but able to retry after React cleans up an abandoned attach attempt.
 */
export const useAttachSquareCard = ({
  squareApplicationId,
  card,
  setCard,
  setLoading,
  container,
}: UseAttachSquareCardArgs): void => {
  const attachingContainer = useRef<HTMLElement | null>(null);
  const cardRef = useRef<Square.Card | undefined>(card);
  const [readyContainer, setReadyContainer] = useState<HTMLElement | null>(
    null
  );

  useEffect(() => {
    cardRef.current = card;
  }, [card]);

  useEffect(() => {
    if (!container) {
      setReadyContainer(null);
      return;
    }

    let animationFrameId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let resizeObserver: ResizeObserver | undefined;

    const updateReadyContainer = () => {
      const rect = container.getBoundingClientRect();
      const nextContainer =
        container.isConnected && rect.width > 0 && rect.height > 0
          ? container
          : null;

      setReadyContainer((currentContainer) =>
        currentContainer === nextContainer ? currentContainer : nextContainer
      );
    };

    if (typeof window.requestAnimationFrame === "function") {
      animationFrameId = window.requestAnimationFrame(updateReadyContainer);
    } else {
      timeoutId = setTimeout(updateReadyContainer, 0);
    }

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateReadyContainer);
      resizeObserver.observe(container);
    }

    return () => {
      if (animationFrameId !== undefined) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      resizeObserver?.disconnect();
      setReadyContainer((currentContainer) =>
        currentContainer === container ? null : currentContainer
      );
    };
  }, [container]);

  useEffect(() => {
    if (
      cardRef.current ||
      !readyContainer ||
      attachingContainer.current === readyContainer
    ) {
      return;
    }
    attachingContainer.current = readyContainer;
    let isActive = true;
    let attachedCard: Square.Card | undefined;

    const attach = async () => {
      setLoading(true);
      try {
        const payments = await Square.payments(squareApplicationId);
        const squareCard = await payments?.card({});
        if (!squareCard) {
          throw new Error("Square payments.card() did not return a card.");
        }
        if (!isActive) {
          await squareCard.destroy();
          return;
        }

        attachedCard = squareCard;
        await squareCard.attach(readyContainer);
        if (!isActive) {
          await squareCard.destroy();
          return;
        }

        setCard(squareCard);
      } catch (error) {
        if (isActive) {
          attachingContainer.current = null;
          console.error("Failed to attach Square card container:", error);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    attach();

    return () => {
      isActive = false;
      attachingContainer.current = null;
      if (attachedCard) {
        attachedCard.detach();
      }
    };
  }, [squareApplicationId, setCard, setLoading, readyContainer]);
};
